import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';
import { useNavigate } from 'react-router-dom';
import MobileStickyCTA from '@/components/feature/MobileStickyCTA';

export default function MethadoneTreatment() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thornhilltreatment.com';

  useSEO({
    title: 'Methadone Treatment Thornhill | Opioid Addiction Recovery',
    description: 'Professional methadone treatment clinic in Thornhill serving Richmond Hill, Vaughan, and North York. Evidence-based opioid addiction treatment with daily supervised dosing, medical monitoring, and comprehensive care. Licensed methadone maintenance program. Call (905) 709-9985 for confidential consultation.',
    keywords: 'methadone treatment Thornhill, methadone clinic Thornhill, methadone maintenance Richmond Hill, methadone program Vaughan, opioid addiction treatment Thornhill, methadone dosing North York, methadone clinic near me, opioid replacement therapy Thornhill, methadone maintenance treatment, addiction recovery Thornhill, methadone pharmacy Richmond Hill, opioid use disorder treatment',
    canonical: `${siteUrl}/methadone-treatment`,
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      "name": "Thornhill Drug-Mart Methadone Treatment Clinic",
      "description": "Professional methadone treatment clinic in Thornhill, Ontario providing comprehensive opioid addiction treatment with daily supervised dosing, medical monitoring, counseling support, and individualized care plans. Serving Thornhill, Richmond Hill, Vaughan, North York, and Greater Toronto Area.",
      "url": `${siteUrl}/methadone-treatment`,
      "telephone": "+1-905-709-9985",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "8108 Yonge St Unit 3-4",
        "addressLocality": "Thornhill",
        "addressRegion": "ON",
        "postalCode": "L4J 1W4",
        "addressCountry": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "43.8184",
        "longitude": "-79.4278"
      },
      "medicalSpecialty": ["Addiction Medicine", "Pharmacy"],
      "availableService": {
        "@type": "MedicalTherapy",
        "name": "Methadone Maintenance Treatment",
        "description": "Evidence-based methadone treatment program for opioid addiction with daily supervised dosing, comprehensive medical monitoring, individualized treatment plans, counseling referrals, and ongoing support for long-term recovery"
      }
    }
  });

  const faqs = [
    {
      question: 'What is methadone and how does it work?',
      answer: 'Methadone is a long-acting medication used to treat opioid addiction. It activates opioid receptors in a slow, steady way. This stops withdrawal symptoms and reduces cravings. It does not cause a "high" like other opioids. A single dose lasts 24 to 36 hours. This makes once-daily dosing effective.'
    },
    {
      question: 'What is the difference between methadone and Suboxone?',
      answer: 'Methadone is a full opioid agonist. Suboxone is a partial agonist. Methadone usually requires daily clinic visits at first. Suboxone often allows take-home doses from the start. Both work well for opioid addiction. Methadone may be better for severe, long-term dependence. Your doctor can help you choose the right one.'
    },
    {
      question: 'Do I have to visit the clinic every day?',
      answer: 'Yes, at first. Most programs require daily clinic visits when you start. Staff watch you take your dose to make sure it\'s correct. They also check how you\'re doing. As you show progress, you can earn take-home doses. This means fewer clinic visits over time.'
    },
    {
      question: 'How long does methadone treatment last?',
      answer: 'Treatment length varies for each person. Some need it for a few months to a year. Others need long-term care. Staying on treatment longer often leads to better results. Many patients stay on methadone for years. It helps them stay stable and live well. Your care team will help decide how long you need treatment.'
    },
    {
      question: 'What are the side effects of methadone?',
      answer: 'Common side effects include constipation, sweating, and drowsiness. Dry mouth, nausea, and sexual issues can also happen. These are usually mild and fade as your body adjusts. Serious side effects are rare when taken as prescribed. Our team watches you closely. We can adjust your dose to reduce side effects while keeping treatment effective.'
    },
    {
      question: 'Can I work while on methadone treatment?',
      answer: 'Yes. Most patients can work, go to school, and live normally. Once your dose is stable, methadone should not make you sleepy or impaired. In fact, treatment often makes it easier to keep a job. It removes withdrawal symptoms and cravings. We work around your schedule to avoid disrupting your daily life.'
    },
    {
      question: 'Is methadone treatment covered by insurance?',
      answer: 'Many insurance plans cover methadone treatment. Ontario health programs may also cover it. Coverage depends on your plan. Call us at (905) 709-9985 to check your options. We want to make treatment affordable for everyone.'
    },
    {
      question: 'Can I stop methadone treatment anytime?',
      answer: 'You can stop, but we strongly recommend working with your doctor. Stopping suddenly can cause severe withdrawal. It also raises the risk of relapse. If you want to stop, your doctor will create a slow tapering plan. This reduces withdrawal symptoms. Many patients choose to stay on methadone long-term because it keeps them stable.'
    }
  ];

  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Long-Acting Relief',
      description: 'Single daily dose provides 24-36 hours of symptom control'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Eliminates Cravings',
      description: 'Effectively reduces opioid cravings and withdrawal symptoms'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Blocks Euphoria',
      description: 'Prevents euphoric effects from other opioids, reducing misuse'
    },
    {
      icon: 'ri-hospital-line',
      title: 'Evidence-Based',
      description: 'Decades of research prove effectiveness for opioid addiction'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Improves Quality of Life',
      description: 'Helps patients regain stability and function in daily activities'
    },
    {
      icon: 'ri-team-line',
      title: 'Comprehensive Support',
      description: 'Includes counseling referrals and ongoing medical monitoring'
    }
  ];

  const treatmentProcess = [
    {
      step: '01',
      title: 'Initial Assessment',
      description: 'Comprehensive evaluation with healthcare provider to determine if methadone is appropriate for your needs',
      icon: 'ri-stethoscope-line'
    },
    {
      step: '02',
      title: 'Induction Phase',
      description: 'Starting with a low dose and gradually increasing over several days to find your optimal dose',
      icon: 'ri-medicine-bottle-line'
    },
    {
      step: '03',
      title: 'Stabilization',
      description: 'Adjusting dose until withdrawal symptoms and cravings are controlled without sedation',
      icon: 'ri-line-chart-line'
    },
    {
      step: '04',
      title: 'Daily Witnessed Dosing',
      description: 'Regular visits to our Thornhill clinic for supervised medication administration',
      icon: 'ri-calendar-check-line'
    },
    {
      step: '05',
      title: 'Counseling Integration',
      description: 'Referrals to counseling and support services to address underlying issues',
      icon: 'ri-user-voice-line'
    },
    {
      step: '06',
      title: 'Take-Home Privileges',
      description: 'Earning take-home doses as you demonstrate stability and progress',
      icon: 'ri-home-heart-line'
    }
  ];

  const whyChoose = [
    {
      icon: 'ri-user-smile-line',
      title: 'Experienced Team',
      description: 'Specialized staff trained in addiction medicine and methadone treatment protocols'
    },
    {
      icon: 'ri-shield-user-line',
      title: 'Confidential Care',
      description: 'Private, judgment-free environment with complete confidentiality'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Convenient Location',
      description: 'Easily accessible Thornhill clinic serving Richmond Hill, Vaughan, and North York'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Comprehensive Support',
      description: 'Medical monitoring, counseling referrals, and ongoing support throughout recovery'
    }
  ];

  const keyFacts = [
    {
      icon: 'ri-check-line',
      title: 'FDA Approved',
      description: 'Approved by FDA for opioid use disorder treatment since the 1960s'
    },
    {
      icon: 'ri-check-line',
      title: 'Highly Regulated',
      description: 'Dispensed through licensed clinics with strict safety protocols'
    },
    {
      icon: 'ri-check-line',
      title: 'Safe When Supervised',
      description: 'Very safe when taken as prescribed under medical supervision'
    },
    {
      icon: 'ri-check-line',
      title: 'Proven Effective',
      description: 'Reduces opioid use and improves treatment retention rates'
    }
  ];

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] sm:min-h-[80vh] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20methadone%20clinic%20interior%20with%20medical%20staff%2C%20clean%20modern%20healthcare%20facility%20with%20teal%20accents%2C%20bright%20natural%20lighting%2C%20organized%20pharmaceutical%20workspace%2C%20compassionate%20addiction%20treatment%20atmosphere%2C%20safe%20clinical%20environment%2C%20supportive%20recovery%20setting%2C%20welcoming%20medical%20consultation%20area&width=1920&height=1000&seq=methadone-hero-001&orientation=landscape"
          alt="Methadone treatment clinic in Thornhill"
          title="Methadone Treatment - Thornhill Drug-Mart"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>

        <div className="relative z-10 w-full px-4 sm:px-6 py-20 sm:py-24">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center mb-6 sm:mb-8" aria-label="Breadcrumb">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center text-white/80 hover:text-white transition-colors cursor-pointer"
                  aria-label="Go to homepage"
                >
                  <i className="ri-home-4-line text-base sm:text-lg"></i>
                  <span className="ml-2 text-xs sm:text-sm font-medium">Home</span>
                </button>
                <i className="ri-arrow-right-s-line text-white/60 text-base sm:text-lg"></i>
                <button
                  onClick={() => navigate('/addiction-services')}
                  className="text-white/80 hover:text-white text-xs sm:text-sm font-medium transition-colors cursor-pointer"
                >
                  Addiction Services
                </button>
                <i className="ri-arrow-right-s-line text-white/60 text-base sm:text-lg"></i>
                <span className="text-primary-400 text-xs sm:text-sm font-semibold">Methadone Treatment</span>
              </div>
            </nav>

            {/* Badge */}
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="inline-flex items-center space-x-2 bg-primary-600/20 backdrop-blur-md px-3 py-1.5 rounded-full">
                <span className="text-primary-300 text-xs font-semibold uppercase tracking-wide">Opioid Replacement Therapy</span>
              </div>
            </div>

            {/* Content */}
            <header className="text-center mb-8 sm:mb-10">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Methadone Maintenance Treatment
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-8 sm:mb-10 max-w-4xl mx-auto leading-relaxed">
                Professional <strong>methadone treatment clinic</strong> in <strong>Thornhill</strong> for effective <strong>opioid addiction recovery</strong>. Daily supervised dosing, medical monitoring, and compassionate care serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong>.
              </p>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto">
                <a
                  href="tel:9057099985"
                  className="bg-primary-600 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-primary-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="Call for methadone treatment consultation"
                >
                  <i className="ri-phone-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">(905) 709-9985</span>
                  <span className="sm:hidden">Call Now</span>
                </a>
                <a
                  href="/location"
                  className="bg-background-50 text-foreground-950 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-background-200/70 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="Visit our Thornhill methadone clinic"
                >
                  <i className="ri-map-pin-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">Visit Us</span>
                  <span className="sm:hidden">Location</span>
                </a>
              </div>
            </header>
          </div>
        </div>
      </section>

      {/* What is Methadone Section */}
      <section className="py-12 sm:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground-950 mb-4 sm:mb-6">
                What is <strong>Methadone Treatment</strong>?
              </h2>
              <p className="text-base sm:text-lg text-foreground-700 leading-relaxed mb-4">
                <strong>Methadone</strong> is a long-acting medication for <strong>opioid addiction</strong>. It activates opioid receptors slowly and steadily. This stops withdrawal symptoms and cravings without causing a "high." Learn more about <a href="/what-is-addiction" className="text-primary-700 hover:text-primary-800 underline underline-offset-2 transition-colors font-semibold">how addiction affects the brain</a> and why medication-assisted treatment works.
              </p>
              <p className="text-base sm:text-lg text-foreground-700 leading-relaxed mb-4">
                Our <strong>methadone clinic</strong> in <strong>Thornhill</strong> provides daily supervised dosing and custom treatment plans. We offer medical monitoring, counseling referrals, and ongoing support. This helps patients achieve lasting <strong>opioid addiction recovery</strong>. For patients seeking an alternative, we also offer <a href="/suboxone-treatment" className="text-primary-700 hover:text-primary-800 underline underline-offset-2 transition-colors font-semibold">Suboxone treatment</a> with more flexible take-home options.
              </p>
              <p className="text-base sm:text-lg text-foreground-700 leading-relaxed">
                Serving patients from <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, <strong>North York</strong>, and throughout the Greater Toronto Area, our licensed <strong>methadone maintenance program</strong> offers evidence-based treatment with compassionate, professional care.
              </p>
            </div>
            <div className="w-full h-96 lg:h-full rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=compassionate%20healthcare%20provider%20consulting%20with%20patient%20about%20methadone%20treatment%2C%20modern%20clinic%20consultation%20room%20with%20teal%20accents%2C%20warm%20professional%20environment%2C%20addiction%20recovery%20counseling%20session%2C%20medication-assisted%20treatment%20discussion%2C%20clean%20bright%20clinical%20space%2C%20empathetic%20care%20setting%2C%20supportive%20healthcare%20atmosphere&width=800&height=600&seq=methadone-what-is-001&orientation=landscape"
                alt="Methadone treatment consultation in Thornhill"
                title="Methadone Consultation - Thornhill Clinic"
                className="w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground-950 mb-3 sm:mb-4">
              Benefits of <strong>Methadone Treatment</strong>
            </h2>
            <p className="text-base sm:text-lg text-foreground-700 max-w-3xl mx-auto">
              <strong>Methadone maintenance treatment</strong> offers proven advantages for <strong>opioid addiction recovery</strong>
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {benefits.map((benefit, index) => (
              <article 
                key={index}
                className="bg-background-50 rounded-xl p-6 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-primary-600 text-white rounded-lg mb-4">
                  <i className={`${benefit.icon} text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-foreground-950 mb-2">{benefit.title}</h3>
                <p className="text-sm sm:text-base text-foreground-700 leading-relaxed">{benefit.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Process Section */}
      <section className="py-12 sm:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground-950 mb-3 sm:mb-4">
              Your <strong>Methadone Treatment</strong> Journey
            </h2>
            <p className="text-base sm:text-lg text-foreground-700 max-w-3xl mx-auto">
              Step-by-step process at our <strong>Thornhill methadone clinic</strong>
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {treatmentProcess.map((step, index) => (
              <article 
                key={index}
                className="relative bg-gradient-to-br from-primary-50 to-white rounded-2xl p-6 sm:p-8 hover:shadow-xl transition-all"
              >
                <div className="absolute top-4 right-4 text-5xl sm:text-6xl font-bold text-primary-100">
                  {step.step}
                </div>
                <div className="relative">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-primary-600 text-white rounded-lg mb-4">
                    <i className={`${step.icon} text-2xl sm:text-3xl`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground-950 mb-3">{step.title}</h3>
                  <p className="text-sm sm:text-base text-foreground-700 leading-relaxed">{step.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Key Facts Section */}
      <section className="py-12 sm:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground-950 mb-3 sm:mb-4">
              <strong>Methadone</strong> Key Facts
            </h2>
            <p className="text-base sm:text-lg text-foreground-700 max-w-3xl mx-auto">
              Important information about <strong>methadone maintenance treatment</strong>
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyFacts.map((fact, index) => (
              <article 
                key={index}
                className="bg-background-50 rounded-xl p-6 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-green-600 text-white rounded-lg mb-4">
                  <i className={`${fact.icon} text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-foreground-950 mb-2">{fact.title}</h3>
                <p className="text-sm sm:text-base text-foreground-700 leading-relaxed">{fact.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-primary-600 to-cyan-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
              Why Choose Our <strong>Thornhill Methadone Clinic</strong>?
            </h2>
            <p className="text-base sm:text-lg text-white/90 max-w-3xl mx-auto">
              Trusted <strong>methadone treatment</strong> provider serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong>
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChoose.map((item, index) => (
              <article 
                key={index}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 hover:bg-white/20 transition-all"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-white text-primary-600 rounded-lg mb-4">
                  <i className={`${item.icon} text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm sm:text-base text-white/80 leading-relaxed">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Methadone vs Other Treatments */}
      <section className="py-12 sm:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground-950 mb-3 sm:mb-4">
              <strong>Methadone</strong> vs Other Treatments
            </h2>
            <p className="text-base sm:text-lg text-foreground-700 max-w-3xl mx-auto">
              Understanding different <strong>opioid addiction treatment</strong> options
            </p>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <article className="bg-gradient-to-br from-primary-50 to-white rounded-2xl p-6 sm:p-8 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-primary-600 text-white rounded-lg mr-4">
                  <i className="ri-medicine-bottle-line text-2xl sm:text-3xl"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground-950">Methadone</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-foreground-700">Full opioid agonist with strong craving control</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-foreground-700">Daily supervised dosing at clinic initially</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-foreground-700">Long-acting: 24-36 hour duration</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-foreground-700">Effective for severe, long-term opioid dependence</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-foreground-700">Decades of proven effectiveness</span>
                </li>
              </ul>
            </article>

            <article className="bg-gradient-to-br from-cyan-50 to-white rounded-2xl p-6 sm:p-8 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-cyan-600 text-white rounded-lg mr-4">
                  <i className="ri-capsule-line text-2xl sm:text-3xl"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground-950">Suboxone</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-foreground-700">Partial opioid agonist with ceiling effect</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-foreground-700">Take-home doses often available from start</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-foreground-700">Lower overdose risk due to ceiling effect</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-foreground-700">More flexible dosing schedule</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-foreground-700">May be preferred for moderate dependence</span>
                </li>
              </ul>
            </article>
          </div>

          <div className="mt-8 text-center">
            <p className="text-base sm:text-lg text-foreground-700 max-w-3xl mx-auto">
              Both <strong>methadone</strong> and <strong>Suboxone</strong> are proven treatments. Your doctor will help choose the right one for you. They will consider your needs, how severe your dependence is, and your personal situation.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground-950 mb-3 sm:mb-4">
              Frequently Asked <span className="text-primary-600">Questions</span>
            </h2>
            <p className="text-base sm:text-lg text-foreground-700">
              Common questions about <strong>methadone treatment</strong> in <strong>Thornhill</strong>
            </p>
          </header>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <article 
                key={index}
                className="bg-background-50 rounded-xl shadow-md overflow-hidden"
                itemScope
                itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-background-100 transition-colors cursor-pointer"
                  aria-expanded={expandedFaq === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 
                    className="text-base sm:text-lg font-semibold text-foreground-950 pr-4"
                    itemProp="name"
                  >
                    {faq.question}
                  </h3>
                  <i className={`ri-arrow-${expandedFaq === index ? 'up' : 'down'}-s-line text-primary-600 text-2xl flex-shrink-0`}></i>
                </button>
                {expandedFaq === index && (
                  <div 
                    id={`faq-answer-${index}`}
                    className="px-6 pb-5"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <p className="text-sm sm:text-base text-foreground-700 leading-relaxed" itemProp="text">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>

        {/* FAQ Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground-950 mb-4 sm:mb-6">
            Start Your <strong>Methadone Treatment</strong> Today
          </h2>
          <p className="text-base sm:text-lg text-foreground-700 mb-8 sm:mb-10 leading-relaxed">
            Take the first step toward <strong>opioid addiction recovery</strong> with professional <strong>methadone treatment</strong> at our <strong>Thornhill clinic</strong>. Our compassionate team provides expert care serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong>. We also offer <a href="/suboxone-treatment" className="text-primary-600 hover:text-primary-800 underline underline-offset-2 transition-colors font-semibold">Suboxone treatment</a> as an alternative and provide <a href="/naloxone-kits" className="text-primary-600 hover:text-primary-800 underline underline-offset-2 transition-colors font-semibold">free naloxone kits</a> for overdose prevention. Call for a confidential consultation.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <a
              href="tel:9057099985"
              className="bg-primary-600 text-white px-6 sm:px-8 py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-primary-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
              aria-label="Call for methadone treatment"
            >
              <i className="ri-phone-line mr-2"></i>
              (905) 709-9985
            </a>
            <a
              href="/addiction-services"
              className="bg-primary-900 text-white px-6 sm:px-8 py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-primary-800 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
              aria-label="View all addiction services"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              All Services
            </a>
          </div>

          <div className="mt-8 sm:mt-10 flex items-center justify-center space-x-2 text-foreground-700">
            <i className="ri-shield-check-line text-xl"></i>
            <p className="text-sm sm:text-base">Confidential and professional care</p>
          </div>
        </div>
      </section>

      <MobileStickyCTA />
      <Footer />
    </div>
  );
}
